/**
 * The capture-history list, in one place.
 *
 * `savedWebsites` is written on every import and persisted to Figma clientStorage, then rehydrated
 * at launch (App.tsx) and read by `ImportConfigurations`, which derives the per-domain settings
 * list from it. Three call sites each rolled their own version of the rule and each got it wrong
 * in a different way, so the persisted copy and the in-memory copy disagreed:
 *
 *   WebsiteCapture  `[...state.savedWebsites, url].filter(dedupe).slice(0, 10)` — appends to the
 *                   END, then keeps the FIRST ten. The reducer prepends. So once ten URLs had
 *                   accumulated the new one sat at index 10 and was sliced straight off: the
 *                   persisted history stopped growing, permanently.
 *
 *   FileImport      `dispatch(addSavedWebsite(url)); emit("SAVE_DATA", "savedWebsites",
 *   ZipImport       state.savedWebsites)` — `state.savedWebsites` is the value from the current
 *                   render's closure, so it is the list from BEFORE the dispatch. The URL just
 *                   added was never the one persisted.
 *
 * The visible effect was in Import Configurations: a domain imported through the File tab, or
 * through the URL tab after the tenth capture, was gone from the settings list after a restart.
 *
 * Both the reducer and every persist site now call this, so the two copies agree by construction
 * rather than by two implementations happening to match.
 */

/** How many entries the capture history keeps. */
export const SAVED_WEBSITES_LIMIT = 10;

/**
 * The history after recording `url`: newest first, no duplicates, capped.
 *
 * A blank entry is dropped rather than stored — it would take one of the ten slots and
 * `domainOf('')` is `''`, which the only consumer filters out.
 */
export function nextSavedWebsites(current: readonly string[], url: string): string[] {
  const entry = (url ?? '').trim();
  if (!entry) return [...current];
  return [entry, ...current.filter((saved) => saved !== entry)].slice(0, SAVED_WEBSITES_LIMIT);
}
